import React, { useState, useEffect } from 'react';
import {
  TextField, Button, Checkbox, FormControlLabel, MenuItem,
  Select, InputLabel, FormControl, Typography, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Chip,
  Divider, Box, IconButton
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

const PAY_MODES = ['Cash', 'Cheque', 'NEFT'];
const ENTRY_TYPES = ['Payment', 'Adjustment'];

// Amounts are intentionally whole numbers only (no decimals / number spinners).
const sanitizeInteger = (value) => String(value ?? '').replace(/\D/g, '');

export default function PaymentCollection({
  billNo: propBillNo = '',
  totalBillAmt: propTotal = 0,
  onPaymentsChange,
  existingPayments = [],
}) {
  const [billNo, setBillNo] = useState(propBillNo);
  const [totalAmt, setTotalAmt] = useState(Number(propTotal) || 0);
  const [receivedAmt, setReceivedAmt] = useState('');
  const [payMode, setPayMode] = useState('');
  const [transactionDetail, setTransactionDetail] = useState('');
  const [entryType, setEntryType] = useState('Payment');
  const [closeBill, setCloseBill] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [payments, setPayments] = useState(existingPayments);

  // edit state
  const [editingId, setEditingId] = useState(null);
  const [editAmt, setEditAmt] = useState('');
  const [editTxn, setEditTxn] = useState('');
  const [editClose, setEditClose] = useState(false);
  const [editError, setEditError] = useState('');

  useEffect(() => { setBillNo(propBillNo); }, [propBillNo]);
  useEffect(() => { setTotalAmt(Number(propTotal) || 0); }, [propTotal]);
  useEffect(() => { setPayments(existingPayments); }, [existingPayments]);

  // ── derived values ────────────────────────────────────────────────────────
  // Payments and adjustments are separate values.
  // Adjustment reduces the outstanding balance but is NOT part of Total Received.
  const totalReceived = payments
    .filter(p => p.entry_type === 'Payment')
    .reduce((s, p) => s + Number(p.received_amount), 0);
  const totalAdjustment = payments
    .filter(p => p.entry_type === 'Adjustment')
    .reduce((s, p) => s + Number(p.received_amount), 0);
  const balance = totalAmt - totalReceived - totalAdjustment;
  const isBillClosed = payments.some(p => p.is_bill_closed);

  // for new entry form
  const isFullPayment =
    entryType === 'Payment' &&
    Number(receivedAmt) > 0 &&
    (totalReceived + totalAdjustment + Number(receivedAmt)) >= totalAmt;

  const isAdjustment = entryType === 'Adjustment';

  // for edit row — balance excluding the row being edited
  const balanceExcludingEdit = editingId
    ? totalAmt
      - payments
          .filter(p => p.id !== editingId && p.entry_type === 'Payment')
          .reduce((s, p) => s + Number(p.received_amount), 0)
      - payments
          .filter(p => p.id !== editingId && p.entry_type === 'Adjustment')
          .reduce((s, p) => s + Number(p.received_amount), 0)
    : 0;

  const isEditFull =
    editingId &&
    Number(editAmt) > 0 &&
    Number(editAmt) >= balanceExcludingEdit;

  // auto check close bill for Payment type only
  useEffect(() => {
    if (isFullPayment) {
      setCloseBill(true);
    } else if (entryType === 'Payment') {
      setCloseBill(false);
    }
    // Adjustment — never auto, user decides
  }, [isFullPayment, entryType]);

  // auto check editClose when edit amount covers full balance
  useEffect(() => {
    if (isEditFull) {
      setEditClose(true);
    }
  }, [isEditFull, editAmt]);

  // ── helper: update state + notify parent ──────────────────────────────────
  const updatePayments = (newPayments) => {
    setPayments(newPayments);
    if (onPaymentsChange) onPaymentsChange(newPayments);
  };

  // ── save new entry ────────────────────────────────────────────────────────
  const handleSave = () => {
    setError('');

    if (!billNo) return setError('Bill No is required.');

    const amt = Number(receivedAmt);
    if (!receivedAmt || isNaN(amt) || amt <= 0)
      return setError('Enter a valid received amount.');

    // cannot exceed total bill amount
    if (amt > balance)
      return setError(`Amount exceeds balance. Maximum allowed: ₹${balance.toFixed(2)}`);

    if (entryType === 'Payment' && !payMode) return setError('Select a payment mode.');

    const newEntry = {
      id: Date.now(),
      bill_no: billNo,
      received_amount: amt,
      pay_mode: entryType === 'Adjustment' ? '' : payMode,
      transaction_detail: transactionDetail,
      entry_type: entryType,
      is_bill_closed: entryType === 'Payment' ? isFullPayment : closeBill,
      created_at: new Date().toISOString(),
    };

    updatePayments([...payments, newEntry]);

    // reset form
    setReceivedAmt('');
    setPayMode('');
    setTransactionDetail('');
    setEntryType('Payment');
    setCloseBill(false);
  };

  // ── edit adjustment ───────────────────────────────────────────────────────
  const startEdit = (p) => {
    setEditingId(p.id);
    setEditAmt(p.received_amount);
    setEditTxn(p.transaction_detail || '');
    setEditClose(p.is_bill_closed);
    setEditError('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditAmt('');
    setEditTxn('');
    setEditClose(false);
    setEditError('');
  };

  const saveEdit = (p) => {
    setEditError('');

    const amt = Number(editAmt);
    if (!editAmt || isNaN(amt) || amt <= 0)
      return setEditError('Enter a valid amount.');

    // cannot exceed total bill amount (excluding this row)
    if (amt > balanceExcludingEdit)
      return setEditError(`Maximum allowed: ₹${balanceExcludingEdit.toFixed(2)}`);

    const updated = {
      ...p,
      received_amount: amt,
      transaction_detail: editTxn,
      is_bill_closed: isEditFull ? true : editClose,
    };

    updatePayments(payments.map(x => x.id === p.id ? updated : x));
    cancelEdit();
  };

  // ── status chip ───────────────────────────────────────────────────────────
  const statusChip = (isBillClosed || balance <= 0)
    ? <Chip icon={<CheckCircleIcon />} label="Bill Closed" color="success" size="small" />
    : <Chip icon={<PendingIcon />} label={`Balance ₹${balance.toFixed(2)}`} color="warning" size="small" />;

  return (
    <Box sx={{ mt: 4, p: 3, border: '1px solid #e0e0e0', borderRadius: 2, backgroundColor: '#fafafa' }}>

      {/* header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" fontWeight={700} color="primary">
          Payment Collection
        </Typography>
        {statusChip}
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* summary */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <TextField size="small" label="Bill No" value={billNo}
          InputProps={{ readOnly: true }} sx={{ width: 140 }} />
        <TextField size="small" label="Total Bill Amount (₹)" value={totalAmt}
          InputProps={{ readOnly: true }} sx={{ width: 200 }} />
        <TextField size="small" label="Total Received (₹)" value={totalReceived.toFixed(2)}
          InputProps={{ readOnly: true }} sx={{ width: 170 }} />
        <TextField size="small" label="Adjustment (₹)" value={totalAdjustment.toFixed(2)}
          InputProps={{ readOnly: true }} sx={{ width: 150 }} />
        <TextField size="small" label="Balance (₹)" value={balance.toFixed(2)}
          InputProps={{ readOnly: true }}
          sx={{ width: 150, '& input': { color: balance > 0 ? '#d32f2f' : '#2e7d32', fontWeight: 700 } }} />
      </Box>

      {/* entry form — hidden once bill is closed */}
      {!isBillClosed && (
        <Box sx={{
          display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center',
          p: 2, backgroundColor: '#fff', border: '1px solid #e3e8ef', borderRadius: 1, mb: 2
        }}>
          <TextField
            size="small"
            label="Received Amount (₹)"
            type="text"
            value={receivedAmt}
            onChange={e => {
              const digits = sanitizeInteger(e.target.value);
              const val = Number(digits || 0);
              // block if exceeds balance
              if (val > balance) return;
              setReceivedAmt(digits);
            }}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 12 }}
            sx={{ width: 180 }}
          />

          <FormControl size="small" sx={{ width: 140 }}>
            <InputLabel>Pay Mode</InputLabel>
            <Select
              value={payMode}
              label="Pay Mode"
              onChange={e => setPayMode(e.target.value)}
              disabled={isAdjustment}
            >
              {PAY_MODES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </Select>
          </FormControl>

          <TextField size="small" label="Transaction Detail / Note"
            value={transactionDetail} onChange={e => setTransactionDetail(e.target.value)}
            sx={{ width: 230 }} />

          <FormControl size="small" sx={{ width: 150 }}>
            <InputLabel>Entry Type</InputLabel>
            <Select value={entryType} label="Entry Type" onChange={e => {
              const nextType = e.target.value;
              setEntryType(nextType);
              setCloseBill(false);
              if (nextType === 'Adjustment') setPayMode('');
            }}>
              {ENTRY_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>

          {/* close bill — only show for Adjustment or full Payment */}
          {(isAdjustment || isFullPayment) && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={isFullPayment ? true : closeBill}
                  onChange={e => setCloseBill(e.target.checked)}
                  color="error"
                  disabled={isFullPayment}
                />
              }
              label={
                <Typography variant="body2" color="error" fontWeight={600}>
                  Close Bill {isFullPayment && '(auto)'}
                </Typography>
              }
            />
          )}

          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ minWidth: 90, height: 40 }}>
            Save
          </Button>
        </Box>
      )}

      {error && (
        <Typography color="error" variant="body2" sx={{ mb: 1 }}>{error}</Typography>
      )}

      {/* payment history table */}
      {payments.length > 0 && (
        <>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 3, mb: 1 }}>
            Payment History
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead sx={{ backgroundColor: '#1976d2' }}>
                <TableRow>
                  {['#', 'Date', 'Amount (₹)', 'Pay Mode', 'Entry Type', 'Transaction Detail', 'Status', 'Action'].map(h => (
                    <TableCell key={h} sx={{ color: '#fff', fontWeight: 700, fontSize: 12 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((p, i) => {
                  const isEditingThis = editingId === p.id;
                  const canEdit = p.entry_type === 'Adjustment';

                  return (
                    <TableRow key={p.id ?? i}
                      sx={{ backgroundColor: p.is_bill_closed ? '#e8f5e9' : 'inherit' }}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {p.created_at ? new Date(p.created_at).toLocaleDateString('en-IN') : '—'}
                      </TableCell>

                      {/* amount — editable in edit mode */}
                      <TableCell sx={{ fontWeight: 600 }}>
                        {isEditingThis ? (
                          <Box>
                            <TextField
                              size="small"
                              type="text"
                              value={editAmt}
                              onChange={e => {
                                const digits = sanitizeInteger(e.target.value);
                                const val = Number(digits || 0);
                                // block if exceeds balance excluding this row
                                if (val > balanceExcludingEdit) return;
                                setEditAmt(digits);
                              }}
                              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 12 }}
                              sx={{ width: 110 }}
                              helperText={`Max: ₹${balanceExcludingEdit.toFixed(2)}`}
                            />
                          </Box>
                        ) : `₹${Number(p.received_amount).toFixed(2)}`}
                      </TableCell>

                      <TableCell>{p.pay_mode}</TableCell>

                      <TableCell>
                        <Chip label={p.entry_type} size="small"
                          color={p.entry_type === 'Adjustment' ? 'secondary' : 'default'}
                          variant="outlined" />
                      </TableCell>

                      {/* transaction detail — editable */}
                      <TableCell sx={{ fontSize: 12 }}>
                        {isEditingThis ? (
                          <TextField size="small" value={editTxn}
                            onChange={e => setEditTxn(e.target.value)} sx={{ width: 160 }} />
                        ) : (p.transaction_detail || '—')}
                      </TableCell>

                      {/* status — close bill editable in edit mode */}
                      <TableCell>
                        {isEditingThis ? (
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={isEditFull ? true : editClose}
                                onChange={e => setEditClose(e.target.checked)}
                                color="error"
                                disabled={isEditFull}
                              />
                            }
                            label={
                              <Typography variant="caption" color="error">
                                Close Bill {isEditFull && '(auto)'}
                              </Typography>
                            }
                          />
                        ) : p.is_bill_closed
                          ? <Chip label="Bill Closed" color="success" size="small" />
                          : <Chip label="Open" color="warning" size="small" variant="outlined" />
                        }
                      </TableCell>

                      {/* action */}
                      <TableCell>
                        {canEdit && (
                          isEditingThis ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <IconButton size="small" color="primary" onClick={() => saveEdit(p)}>
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={cancelEdit}>
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Box>
                              {editError && (
                                <Typography color="error" variant="caption">{editError}</Typography>
                              )}
                            </Box>
                          ) : (
                            <IconButton size="small" color="primary" onClick={() => startEdit(p)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          )
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}

                {/* totals row */}
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell colSpan={2} sx={{ fontWeight: 700 }}>Total Received</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>₹{totalReceived.toFixed(2)}</TableCell>
                  <TableCell colSpan={5} />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}